// Config panel for the "incoming_call" trigger block
import React from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Alert
} from '@mui/material';

const CONTEXTS = [
  { value: '', label: 'Any context' },
  { value: 'caro-internal', label: 'Internal (residents)' },
  { value: 'caro-videophone', label: 'Videophone (entrance panels)' },
  { value: 'caro-staff', label: 'Staff' },
  { value: 'caro-emergency', label: 'Emergency' },
];

const IncomingCallConfig = ({ config, onChange, onSummaryChange }) => {
  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);

    // Build summary
    const parts = [];
    if (updated.didPattern) parts.push(`DID ${updated.didPattern}`);
    if (updated.callerPattern) parts.push(`from ${updated.callerPattern}`);
    if (updated.calleePattern) parts.push(`to ${updated.calleePattern}`);
    if (updated.contextFilter) parts.push(`[${updated.contextFilter}]`);
    onSummaryChange(parts.length > 0 ? parts.join(' ') : 'Any call');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Triggers when an incoming call arrives, before Asterisk routes it.
        The automation can then decide to route, reject, or send to voicemail.
      </Typography>

      <TextField
        size="small"
        label="DID Pattern (Inbound Number)"
        value={config.didPattern || ''}
        onChange={(e) => handleChange('didPattern', e.target.value)}
        placeholder="e.g. 93302450 or 9330245*"
        helperText="Filter by inbound DID (the external number dialed via 3CX). Leave empty for any."
      />

      <TextField
        size="small"
        label="Caller Pattern"
        value={config.callerPattern || ''}
        onChange={(e) => handleChange('callerPattern', e.target.value)}
        placeholder="e.g. 7??? or 1*"
        helperText="Filter by caller extension. Leave empty for any caller."
      />

      <TextField
        size="small"
        label="Callee Pattern"
        value={config.calleePattern || ''}
        onChange={(e) => handleChange('calleePattern', e.target.value)}
        placeholder="e.g. 6??? or 9001"
        helperText="Filter by destination extension. Leave empty for any destination."
      />

      <TextField
        size="small"
        select
        label="Context"
        value={config.contextFilter || ''}
        onChange={(e) => handleChange('contextFilter', e.target.value)}
        helperText="Filter by Asterisk context (caller type)"
      >
        {CONTEXTS.map((ctx) => (
          <MenuItem key={ctx.value} value={ctx.value}>{ctx.label}</MenuItem>
        ))}
      </TextField>

      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
        <strong>Pattern syntax:</strong> <code>?</code> = any single character,{' '}
        <code>*</code> = any characters.<br />
        Examples: <code>7???</code> matches 7000-7999, <code>1*</code> matches any ext starting with 1.
      </Alert>

      <Alert severity="success" variant="outlined" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.7rem' } }}>
        <strong>Available variables in downstream blocks:</strong><br />
        <code>{'{{caller}}'}</code> — caller extension or external phone number<br />
        <code>{'{{callee}}'}</code> — destination extension (after DID resolution)<br />
        <code>{'{{callerName}}'}</code> — caller display name<br />
        <code>{'{{did}}'}</code> — inbound DID (external number that was dialed via 3CX)<br />
        <code>{'{{context}}'}</code> — Asterisk context
      </Alert>
    </Box>
  );
};

export default IncomingCallConfig;
